<template>
  <div class="system-card-wrapper">
    <div class="system-card-container">
      <div class="system-card-header">
        <div class="qrw-heading-lg bold qrw-text-content-base-inverse-primary">
          {{uiModeDisplayTitle}}
        </div>
        <div
          class="header-toggle-container"
          v-if="isSystemAvailableInBillingPlan"
        >
          <div
            class="qrw-label-lg bold qrw-text-content-warning-primary"
            v-if="closeRuleUntilString"
          >
            {{$t('superbilling_system_card.inactive')}} ({{$t('schedule.to')}} {{closeRuleUntilString}})
          </div>
          <div
            class="qrw-label-lg bold qrw-text-content-success-primary"
            v-else-if="modeToggle"
          >
            {{$t('superbilling_system_card.active')}}
          </div>
          <div
            class="qrw-label-lg bold qrw-text-content-base-inverse-tertiary"
            v-else
          >
            {{$t('superbilling_system_card.inactive')}}
          </div>

          <BaseBetaToggle
            :value="false"
            @input="onModeToggleInput(true)"
            size="sm"
            v-if="closeRuleUntilString"
          />
          <BaseBetaToggle
            :value="modeToggle"
            @input="onModeToggleInput"
            size="sm"
            v-else
          />
        </div>
      </div>
      <div class="system-card-body">
        <template v-for="(feature,index) in uiFeaturesList">
          <div
            class="gap-row-zero"
            :key="index"
          >
            <div class="qrw-paragraph-md bold qrw-text-content-base-inverse-primary">
              {{$t(`new_systems_features.${feature}.0`)}}
            </div>
            <div
              class="qrw-paragraph-md qrw-text-content-base-inverse-secondary"
              v-html="$t(`new_systems_features.${feature}.1`)"
              v-if="$t(`new_systems_features.${feature}`).length > 1"
            >
            </div>
          </div>
          <BaseDivider
            color="border-base-inverse-tertiary"
            v-if="index != uiFeaturesList.length-1"
            :key="`d${index}`"
          />
        </template>

      </div>

      <div
        class="system-card-billing-banner"
        v-if="!isSystemAvailableInBillingPlan"
      >
        <div class="gap-row-2xs qrw-text-content-base-inverse-primary">
          <div class="qrw-label-lg bold">
            {{$t('superbilling_system_card.banner_title')}}
          </div>
          <div class="qrw-paragraph-sm">
            {{$t('superbilling_system_card.banner_text')}}
          </div>
        </div>
        <BasePushButton
          variant="fill"
          bgColor="background-base-inverse-primary"
          textColor="content-base-inverse-primary"
          size="sm"
          @click="$router.push('/superbilling')"
        >
          {{$t('superbilling_system_card.banner_btn')}}
        </BasePushButton>
      </div>
    </div>
    <transition name="slide">
      <div
        class="system-card-settings"
        v-if="modeToggle && isSystemAvailableInBillingPlan"
      >
        <div class="system-card-settings-header">
          <div class="qrw-heading-xs bold qrw-text-content-base-primary">
            {{$t('superbilling_system_card.checklist_title')}}
          </div>
          <div class="qrw-label-sm qrw-text-content-base-secondary">
            {{$t('superbilling_system_card.checklist_hint')}}
          </div>
        </div>
        <div class="system-card-settings-list">
          <div
            class="settings-item"
            v-for="(entry, index) in requiredSettings"
            @click="openSettingEntry(entry)"
            :key="index"
          >
            <div class="gap-column-txs w-100">
              <div class="system-circle-wrapper">
                <BaseIcon
                  icon="check-circle-filled"
                  class="qrw-text-content-success-primary"
                  size="sm"
                  v-if="entry.done"
                />
                <BaseIcon
                  icon="check-circle"
                  class="qrw-text-content-base-primary"
                  size="sm"
                  v-else
                />
              </div>

              <div class="gap-row-2xs">
                <div
                  class="qrw-label-lg bold"
                  :class="{
                'qrw-text-content-success-primary': entry.done,
                'qrw-text-content-base-primary': !entry.done
              }"
                >
                  {{ $t("mode_settings." + entry.key) }}
                </div>
                <div class="qrw-paragraph-sm qrw-text-content-base-secondary">
                  {{ $t("mode_settings_hints." + entry.key) }}
                </div>
              </div>
            </div>
            <BaseIconButton
              variant="gray"
              textColor="content-base-secondary"
              icon="settings-02"
              size="sm"
            />
          </div>
        </div>
      </div>
    </transition>

    <DisableSystemModal
      style="position:absolute;"
      :system="mode"
      @manual="disableManually"
      @close="disablingSystem = false"
      v-if="disablingSystem"
    />
  </div>
</template>

<script>
import DisableSystemModal from '@/components/modals/DisableSystemModal.vue'

import moment from 'moment'

export default {
  props: {
    mode: String
  },
  data() {
    return {
      disablingSystem: false
    }
  },
  components: {
    DisableSystemModal
  },
  methods: {
    openSettingEntry(entry) {
      const key = entry.key
      const route =
        {
          menu: 'menus',
          schedule: 'general',
          tables: 'halls'
        }[key] || this.mode

      this.$router.push(route)
    },
    disableManually() {
      this.modeToggle = false
    },
    onModeToggleInput(arg) {
      if (arg) {
        //enabling mode can be done without any modals
        this.$store.commit('clearCafeCloseRulesForSystem', this.mode)
        this.modeToggle = true
      } else {
        this.disablingSystem = true
      }
    }
  },
  computed: {
    uiFeaturesList() {
      return (
        {
          preview: ['custom_page', 'menu_translation', 'pos_integration'],
          inside: [
            'inside_orders',
            'orders_panel',
            'online_payment',
            'online_tips',
            'ai',
            'custom_page',
            'pos_integration',
            'crm'
          ],
          delivery: [
            'delivery_orders',
            'sms_verification',
            'push_notifications',
            'orders_panel',
            'online_payment',
            'online_tips',
            'ai',
            'custom_page',
            'pos_integration',
            'crm'
          ],
          preorder: [
            'takeaway_orders',
            'sms_verification',
            'push_notifications',
            'orders_panel',
            'online_payment',
            'online_tips',
            'ai',
            'custom_page',
            'pos_integration',
            'crm'
          ],
          reserve: [
            'reserve_orders',
            'sms_verification',
            'push_notifications',
            'reserve_deposit',
            'reserve_calendar',
            'orders_panel',
            'online_payment',
            'online_tips',
            'ai',
            'custom_page',
            'pos_integration',
            'crm'
          ],
          pro: ['custom_page', 'pro_inside_orders', 'pro_takeaway_orders', 'pro_delivery_orders', 'pro_reserve_orders']
        }[this.mode] || []
      )
    },
    uiModeDisplayTitle() {
      return this.$store.getters['subscriptions/getModeDisplayTitle'](this.mode)
    },
    cafe() {
      return this.$store.state.cafe
    },
    modeToggle: {
      get() {
        if (!this.cafe || !this.cafe.modes) return false

        return this.cafe.modes[this.mode]
      },
      set(value) {
        this.$store.commit('toggleCafeMode', {
          mode: this.mode,
          value
        })
        this.$store.dispatch('saveCafe')

        this.$store.dispatch('auditlog/write', {
          type: value ? 'mode_enable' : 'mode_disable',
          payload: {
            mode: this.mode
          }
        })

        CafeAPI.setMode(this.cafe._id, this.mode, value).then(data => {
          if (data.changed) {
            this.$store.dispatch('subscriptions/fetchAll', this.cafe._id)
          }
        })
      }
    },
    readyness() {
      const key = this.mode.substring(0, 1).toUpperCase() + this.mode.substring(1)

      const value = Object.assign({}, this.$store.getters[`ready${key}`])
      const total = value.total

      delete value.total
      delete value.subtotal

      return value
    },
    requiredSettings() {
      const readynessEntries = Object.entries(this.readyness).map(e => ({
        done: e[1],
        key: e[0]
      }))

      readynessEntries.sort((a, b) => {
        return a.done ? 1 : -1
      })

      return readynessEntries
    },
    isSystemAvailableInBillingPlan() {
      const key = this.mode === 'preorder' ? 'takeaway' : this.mode

      return this.$store.getters.checkBillingPlanForPaidService(key)
    },
    closeRuleUntilString() {
      const rule = this.$store.getters.getCloseRuleForSystem(this.mode)

      return rule ? moment(rule.end_date).format('DD.MM HH:mm') : null
    }
  }
}
</script>

<style scoped>
.system-card-wrapper {
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  height: 360px;
  min-height: 360px;
  max-height: 360px;

  margin-bottom: 24px;
}

.system-card-container {
  width: 100%;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--qrw-shadow-card-light);
  background: var(--qrw-background-base-inverse-primary);
}

.system-card-header {
  display: flex;
  padding: 16px;
  box-sizing: border-box;
  background: var(--qrw-background-base-inverse-secondary);
  align-items: center;
  justify-content: space-between;
  border-radius: 8px 8px 0 0;
}

.header-toggle-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 16px;
}

.system-card-body {
  height: 100%;
  overflow-y: scroll;
  padding: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  background: var(--qrw-background-base-inverse-primary);
  border-radius: 0 0 8px 8px;
}

.system-card-settings {
  width: 400px;
  max-width: 400px;
  min-width: 400px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  box-shadow: var(--qrw-shadow-card-light);
}

.system-card-settings-header {
  display: flex;
  padding: 16px;
  box-sizing: border-box;
  flex-direction: column;
  row-gap: 4px;
  background: var(--qrw-background-base-secondary);
  justify-content: center;
}

.system-card-settings-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
  background: var(--qrw-background-base-primary);
}

.settings-item {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid var(--qrw-border-base-tertiary);
  align-items: center;
  column-gap: 16px;
  padding: 16px;
  box-sizing: border-box;
  cursor: pointer;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s;
}

.slide-enter,
.slide-leave-to {
  width: 0px;
  opacity: 0;
}

.system-card-billing-banner {
  display: flex;
  flex-direction: row;
  column-gap: 16px;
  padding: 12px 16px;
  background: var(--qrw-background-warning-default);
  box-sizing: border-box;
  border-radius: 0 0 8px 8px;
  align-items: center;
  justify-content: space-between;
}

.system-circle-wrapper {
  box-sizing: border-box;
  padding: 2px 0;
}
</style>